/* ============================================================
   SAUNA TRAVEL — reservation availability adapter
   既存 App Proxy API (reservation-statuses) を週範囲で取得し、
   週単位キャッシュ・隣接週先読み・連打中断・週グリッド導出を行う。
   バックエンドは未改修（レスポンス契約はそのまま利用）。

   レスポンス契約:
     GET {base}/products/{productId}/variants/{variantId}/reservation-statuses
         ?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
     => { "YYYY-MM-DD": { availability: "◎|○|△|-",
                          timeSlots: [{ start_at, end_at, end_at_with_buffer }] } }

   公開: window.ReservationAvailability.create({ base, productId })
   ============================================================ */
;(function () {
  'use strict'

  var DAY_MS = 24 * 60 * 60 * 1000

  function pad(n) {
    return String(n).padStart(2, '0')
  }
  function ymd(d) {
    return (
      d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    )
  }
  function startOfDay(d) {
    var x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x
  }
  function addDays(d, n) {
    var x = new Date(d)
    x.setDate(x.getDate() + n)
    return x
  }
  // 日曜起点（既存グリッドのヘッダー 日月火… に合わせる）
  function startOfWeek(d) {
    var x = startOfDay(d)
    x.setDate(x.getDate() - x.getDay())
    return x
  }
  // "2025-07-01 10:00" / "2025-07-01T10:00:00" 双方を Date 化
  function parseApiDate(s) {
    if (!s) return null
    return new Date(s.replace(' ', 'T'))
  }
  function hhmm(s) {
    var d = parseApiDate(s)
    if (!d || isNaN(d.getTime())) {
      // フォールバック: 文字列から HH:MM を拾う
      var m = String(s).match(/(\d{1,2}):(\d{2})/)
      return m ? pad(+m[1]) + ':' + m[2] : ''
    }
    return pad(d.getHours()) + ':' + pad(d.getMinutes())
  }
  // parseApiDate は日付のみの文字列を UTC 解釈するため、営業日キーの解釈には使えない。
  function parseYmd(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s))
    if (!m) return null
    return new Date(+m[1], +m[2] - 1, +m[3])
  }
  /**
   * 営業日 0:00 を基準にした枠開始時刻の経過分。
   * API は深夜営業でも営業日のキーに翌日の start_at を入れて返すため、時刻だけに
   * 丸めると営業日とのズレを表現できない。
   * @returns {number|null} 営業日より前の枠は null（行に出さない＝fail-closed）
   */
  function slotOffsetMinutes(dayIso, startAt) {
    var base = parseYmd(dayIso)
    var d = parseApiDate(startAt)
    if (base && d && !isNaN(d.getTime())) {
      var dayDiff = Math.round(
        (startOfDay(d).getTime() - base.getTime()) / DAY_MS
      )
      if (dayDiff < 0) return null
      return dayDiff * 24 * 60 + d.getHours() * 60 + d.getMinutes()
    }
    // 日時にならない値を null に倒すと壊れた枠が行ごと消えて「枠が無い日」に見える。
    // 行には出し、選択可否はセル側の hasValidStart に委ねる。
    var hm = hhmm(startAt).split(':')
    return hm.length === 2 ? +hm[0] * 60 + +hm[1] : null
  }
  // 経過分を 24 時間超表記にする（1440 -> "24:00" / 1590 -> "26:30"）。
  function formatOffset(mins) {
    if (mins == null || !isFinite(mins)) return ''
    var h = Math.floor(mins / 60)
    return pad(h) + ':' + pad(mins - h * 60)
  }
  // グリッドの行キー兼表示ラベル。営業日基準の 24 時間超表記。
  function slotKey(dayIso, startAt) {
    return formatOffset(slotOffsetMinutes(dayIso, startAt))
  }

  // 空き段階の強さ（横断集約用）。◎ > ○ > △ > − の順で最も空きのある段階を採用。
  // 未知/欠損は -1（= '-' 扱い）。
  var LEVEL_RANK = { '◎': 3, '○': 2, '△': 1, '-': 0 }
  function levelRank(l) {
    return Object.prototype.hasOwnProperty.call(LEVEL_RANK, l)
      ? LEVEL_RANK[l]
      : -1
  }
  function rankToLevel(rank) {
    if (rank < 0) return '-'
    for (var sym in LEVEL_RANK) {
      if (LEVEL_RANK[sym] === rank) return sym
    }
    return '-'
  }

  // 同時実行数を上限付きで制御するプール実行。worker が 1 つでも reject したら
  // 即座に全体を reject する（fail-closed の起点）。items 空なら即 resolve。
  function runPool(items, limit, worker) {
    var n = items.length
    if (n === 0) return Promise.resolve()
    return new Promise(function (resolve, reject) {
      var idx = 0
      var running = 0
      var settled = false
      function fail(err) {
        if (settled) return
        settled = true
        reject(err)
      }
      function launchNext() {
        if (settled) return
        if (idx >= n && running === 0) {
          settled = true
          resolve()
          return
        }
        while (running < limit && idx < n && !settled) {
          running++
          worker(items[idx++]).then(function () {
            running--
            launchNext()
          }, fail)
        }
      }
      launchNext()
    })
  }

  function create(opts) {
    opts = opts || {}
    var base = (opts.base || '/apps/api').replace(/\/$/, '')
    var productId = opts.productId
    var ttlMs = opts.ttlMs || 10 * 60 * 1000 // 既存同様 10 分でキャッシュ失効
    // 横断フェッチの同時実行上限（App Proxy のスロットリング回避。design DD6）。
    var multiConcurrency = opts.multiConcurrency || 6

    // cache key: variantId + '|' + weekStartYmd  ->  { at, days }
    var cache = {}
    var inflight = {} // 同一キーの重複リクエスト抑止
    var controllers = {} // weekKey -> AbortController（連打中断用）

    function weekKey(variantId, weekStart) {
      return variantId + '|' + ymd(weekStart)
    }

    function fresh(entry) {
      return entry && Date.now() - entry.at < ttlMs
    }

    function request(variantId, weekStart, forceRefresh) {
      var key = weekKey(variantId, weekStart)
      // forceRefresh 時はキャッシュ済み応答を使わず必ずサーバへ問い合わせる
      // （確定直前の fail-closed 再検証など、最新性が必須の経路向け）。
      // inflight は進行中の実ネットワーク要求なので流用してよい。
      if (!forceRefresh && fresh(cache[key])) return Promise.resolve(cache[key].days)
      if (inflight[key]) return inflight[key]

      var start = ymd(weekStart)
      var end = ymd(addDays(weekStart, 6))
      var url =
        base +
        '/products/' +
        encodeURIComponent(productId) +
        '/variants/' +
        encodeURIComponent(variantId) +
        '/reservation-statuses?start_date=' +
        start +
        '&end_date=' +
        end

      // 旧コントローラを中断してから新規発行（連打対策）
      if (controllers[key]) controllers[key].abort()
      var ctrl =
        typeof AbortController !== 'undefined' ? new AbortController() : null
      controllers[key] = ctrl

      var p = fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
        .then(function (res) {
          // 非JSONなエラー応答(HTML/空ボディ等)でも status/種別を保てるよう、
          // text() で受けてから安全に JSON.parse する（失敗してもエラー分類は維持）。
          return res.text().then(function (raw) {
            var data = {}
            var parseError = null
            try {
              data = raw ? JSON.parse(raw) : {}
            } catch (e) {
              parseError = e
              data = {}
            }
            if (!res.ok) {
              var err = new Error('HTTP ' + res.status)
              err.kind = res.status >= 400 && res.status < 500 ? 'client' : 'server'
              err.status = res.status
              err.details = data && data.error
              throw err
            }
            // 2xx でも本文が壊れた JSON の場合は「取得成功・空」としてキャッシュせず、
            // 取得失敗扱いにして再試行導線へ乗せる。
            if (parseError) {
              var invalidErr = new Error('Invalid reservation-statuses response')
              invalidErr.kind = 'server'
              throw invalidErr
            }
            return data || {}
          })
        })
        .then(function (data) {
          cache[key] = { at: Date.now(), days: data }
          delete inflight[key]
          return data
        })
        .catch(function (err) {
          delete inflight[key]
          if (err && err.name === 'AbortError') {
            var abortErr = new Error('aborted')
            abortErr.kind = 'abort'
            throw abortErr
          }
          if (!err.kind) err.kind = 'network'
          throw err
        })

      inflight[key] = p
      return p
    }

    /**
     * 指定週の空き状況を取得する。
     * @returns Promise<{ weekStart, days }>
     */
    function fetchWeek(input) {
      var variantId = input.variantId
      var weekStart = startOfWeek(input.weekStart)
      return request(variantId, weekStart, input.forceRefresh).then(function (
        days
      ) {
        return { weekStart: weekStart, days: days }
      })
    }

    /** 前後週を裏で先読み（失敗は握りつぶす） */
    function prefetchAdjacentWeeks(input) {
      var variantId = input.variantId
      var weekStart = startOfWeek(input.weekStart)
      ;[addDays(weekStart, 7), addDays(weekStart, -7)].forEach(function (ws) {
        if (ws < startOfWeek(new Date())) return // 過去週は先読みしない
        request(variantId, ws).catch(function () {})
      })
    }

    /**
     * 週グリッドを導出する。
     * 行（時間帯）= 表示週の API timeSlots に実在する開始時刻の和集合のみ
     * （クライアント側で候補枠を計算しない＝バックエンド由来・店舗非依存。design.md 契約）。
     * これによりプラン固有パターン（例: 深夜=1枠）も自動追従し、存在しない時間帯を「満」と誤表示しない。
     * セル状態: ○=当日空き枠に在る / -=実在枠だが当日は受付不可 / closed=その日が全枠不可。
     * 行キー slotStart は営業日 0:00 基準の 24 時間超表記（深夜営業の翌 0:00 枠 = "24:00"）。
     * 暦日に丸めないため、日またぎ枠が前日の 0:00 行に落ちない。
     * @returns { weekStart, days:Date[], header:[...], rows:[{slotStart, cells:[...]}] }
     */
    function deriveGrid(week, optsArg) {
      var now = (optsArg && optsArg.now) || new Date()
      var weekStart = week.weekStart
      var data = week.days || {}
      var days = []
      for (var i = 0; i < 7; i++) days.push(addDays(weekStart, i))

      // 1) 行となる開始時刻 = 表示週の実在 timeSlots 開始時刻の和集合のみ
      //    キーは営業日基準の 24 時間超表記（深夜枠 = 24:00 など）。値は並び順の正となる経過分。
      var startSet = {}
      days.forEach(function (d) {
        var dayIso = ymd(d)
        var entry = data[dayIso]
        if (entry && entry.timeSlots) {
          entry.timeSlots.forEach(function (s) {
            var mins = slotOffsetMinutes(dayIso, s.start_at)
            var t = formatOffset(mins)
            if (t) startSet[t] = mins
          })
        }
      })
      // 文字列ソートは時の桁数が揃わないと破綻するため経過分で並べる
      var slotStarts = Object.keys(startSet).sort(function (a, b) {
        return startSet[a] - startSet[b]
      })

      // 2) 各行 × 各日でセル状態を決める
      var rows = slotStarts.map(function (slotStart) {
        var cells = days.map(function (d) {
          var key = ymd(d)
          var entry = data[key]
          var dayLevel = entry ? entry.availability : '-'
          var match = null
          if (entry && entry.timeSlots) {
            for (var j = 0; j < entry.timeSlots.length; j++) {
              if (slotKey(key, entry.timeSlots[j].start_at) === slotStart) {
                match = entry.timeSlots[j]
                break
              }
            }
          }
          var state, selectable
          if (dayLevel === '-' || !entry) {
            state = 'closed'
            selectable = false
          } else if (match) {
            var startDate = parseApiDate(match.start_at)
            var startTime = startDate ? startDate.getTime() : NaN
            var hasValidStart = isFinite(startTime)
            // start_at を解釈できない壊れた枠は fail closed（選択不可）にする
            var past = !hasValidStart || startTime <= now.getTime()
            state = '○'
            selectable = hasValidStart && !past
          } else {
            state = '-'
            selectable = false
          }
          return {
            date: d,
            iso: key,
            state: state,
            slot: match,
            selectable: selectable,
          }
        })
        return { slotStart: slotStart, cells: cells }
      })

      var header = days.map(function (d) {
        var entry = data[ymd(d)]
        return {
          date: d,
          iso: ymd(d),
          wd: d.getDay(),
          level: entry ? entry.availability : '-',
          isToday: ymd(d) === ymd(startOfDay(now)),
          // API 応答の素通し。既定値へのフォールバック等の解釈は呼び出し側に置く。
          pricingCategory: entry ? entry.pricingCategory : undefined,
        }
      })

      return { weekStart: weekStart, days: days, header: header, rows: rows }
    }

    // 「未定義 vs 定義あり」は不一致として扱わない。variant 側で reservableDaysAhead /
    // daysOffWeek を上書きしている商品では、ある variant の応答にだけその日が含まれない。
    function pickPricingCategory(byVariant, variantIds, key) {
      var picked, pickedVid
      variantIds.forEach(function (vid) {
        var entry = (byVariant[vid] || {})[key]
        var cat = entry ? entry.pricingCategory : undefined
        if (cat === undefined || cat === null) return
        if (picked === undefined) {
          picked = cat
          pickedVid = vid
        } else if (cat !== picked) {
          console.warn(
            '[pricing-category] conflicting pricingCategory on ' +
              key +
              ': ' +
              pickedVid +
              '=' +
              picked +
              ' / ' +
              vid +
              '=' +
              cat
          )
        }
      })
      return picked
    }

    // 1 バリアントの週取得を「失敗時1回リトライ」で包む。
    // AbortError（週連打中断, err.kind==='abort'）はリトライせず即座に伝播する。
    function requestWithRetry(variantId, weekStart) {
      return request(variantId, weekStart).catch(function (err) {
        if (err && err.kind === 'abort') throw err
        // 1 回だけ再試行（瞬断・スロットリングの緩和）
        return request(variantId, weekStart)
      })
    }

    /**
     * 複数の代表バリアントの同一週を横断取得する（calendar_first STEP1 用）。
     * 同時実行は multiConcurrency 上限のプールで制御。各バリアントは1回リトライし、
     * リトライ後も1本でも失敗なら週全体を reject（fail-closed＝部分描画で空き枠を
     * 見落とさない）。AbortError は即伝播。
     * @param {{ variants: {variantId, planType?, extraKey?}[], weekStart: Date }} input
     * @returns Promise<{ weekStart: Date, byVariant: Record<string, DaysMap> }>
     */
    function fetchWeekMulti(input) {
      var weekStart = startOfWeek(input.weekStart)
      var variants = (input && input.variants) || []
      // 重複バリアントを除去（前提: 代表は planType×extraKey 単位で一意だが念のため）
      var seen = {}
      var list = []
      variants.forEach(function (rv) {
        var id = rv && rv.variantId
        if (id == null || seen[id]) return
        seen[id] = true
        list.push(rv)
      })

      var byVariant = {}
      return runPool(list, multiConcurrency, function (rv) {
        return requestWithRetry(rv.variantId, weekStart).then(function (days) {
          byVariant[rv.variantId] = days
        })
      }).then(function () {
        return { weekStart: weekStart, byVariant: byVariant }
      })
    }

    /**
     * 横断週グリッドを導出する（既存 deriveGrid と同一のセル契約 + reps を付与）。
     * 行（時間帯）= 全バリアント横断で実在する開始時刻の和集合。
     * 各セルは「いずれかのバリアントが選択可能」なら selectable:true（OR）。
     * reps = その開始時刻・その日に予約可能な枠を持つ {variantId, slot} 配列（STEP2 用）。
     * 日ヘッダ level = 当日の各バリアント availability を ◎>○>△>− で最大採用（横断集約）。
     * 過去・受付不可枠は selectable:false（既存 deriveGrid と同一の fail-closed 判定）。
     * @param {{ weekStart: Date, byVariant: Record<string, DaysMap> }} week
     * @param {{ now?: Date, reps?: {variantId}[] }} optsArg
     * @returns { weekStart, days:Date[], header:[...], rows:[{slotStart, cells:[...]}] }
     */
    function deriveGridMulti(week, optsArg) {
      var now = (optsArg && optsArg.now) || new Date()
      var byVariant = (week && week.byVariant) || {}
      var reps = (optsArg && optsArg.reps) || []
      var variantIds = reps.length
        ? reps.map(function (r) {
            return r.variantId
          })
        : Object.keys(byVariant)
      var weekStart = week.weekStart

      var days = []
      for (var i = 0; i < 7; i++) days.push(addDays(weekStart, i))

      // 1) 行となる開始時刻 = 全バリアント横断の実在 timeSlots 開始時刻の和集合
      //    キー・並び順の扱いは deriveGrid と同一（営業日基準の 24 時間超表記 / 経過分で昇順）。
      var startSet = {}
      variantIds.forEach(function (vid) {
        var data = byVariant[vid] || {}
        days.forEach(function (d) {
          var dayIso = ymd(d)
          var entry = data[dayIso]
          if (entry && entry.timeSlots) {
            entry.timeSlots.forEach(function (s) {
              var mins = slotOffsetMinutes(dayIso, s.start_at)
              var t = formatOffset(mins)
              if (t) startSet[t] = mins
            })
          }
        })
      })
      var slotStarts = Object.keys(startSet).sort(function (a, b) {
        return startSet[a] - startSet[b]
      })

      // 2) 各行 × 各日: バリアント横断で OR 集約し reps を付与
      var rows = slotStarts.map(function (slotStart) {
        var cells = days.map(function (d) {
          var key = ymd(d)
          var anyOpen = false // いずれかが ○（枠が実在）
          var anyDash = false // いずれかが −（当日受付中だがこの時刻に枠なし）
          var selectableReps = []
          variantIds.forEach(function (vid) {
            var data = byVariant[vid] || {}
            var entry = data[key]
            var dayLevel = entry ? entry.availability : '-'
            if (dayLevel === '-' || !entry) return // このバリアントは当日 closed
            var match = null
            if (entry.timeSlots) {
              for (var j = 0; j < entry.timeSlots.length; j++) {
                if (slotKey(key, entry.timeSlots[j].start_at) === slotStart) {
                  match = entry.timeSlots[j]
                  break
                }
              }
            }
            if (match) {
              anyOpen = true
              var startDate = parseApiDate(match.start_at)
              var startTime = startDate ? startDate.getTime() : NaN
              var hasValidStart = isFinite(startTime)
              var past = !hasValidStart || startTime <= now.getTime()
              if (hasValidStart && !past) {
                selectableReps.push({ variantId: vid, slot: match })
              }
            } else {
              anyDash = true
            }
          })
          // 選択可能な rep が1つも無ければ ○ にしない（過去/締切のみの実在枠を
          // 空きに見せない）。実在枠はあるが選べない場合は −（受付不可/満/過去）。
          var selectable = selectableReps.length > 0
          var state = selectable
            ? '○'
            : anyOpen || anyDash
              ? '-'
              : 'closed'
          return {
            date: d,
            iso: key,
            state: state,
            selectable: selectable,
            // 表示互換: 選択可能なら代表 rep の slot を載せる（calendar_first は reps を使う）
            slot: selectable ? selectableReps[0].slot : null,
            reps: selectableReps,
          }
        })
        return { slotStart: slotStart, cells: cells }
      })

      // 3) 日ヘッダ level = 当日各バリアント availability の最大段階（横断集約）
      var header = days.map(function (d) {
        var key = ymd(d)
        var best = -1
        variantIds.forEach(function (vid) {
          var entry = (byVariant[vid] || {})[key]
          if (entry) {
            var r = levelRank(entry.availability)
            if (r > best) best = r
          }
        })
        return {
          date: d,
          iso: key,
          wd: d.getDay(),
          level: rankToLevel(best),
          isToday: ymd(d) === ymd(startOfDay(now)),
          pricingCategory: pickPricingCategory(byVariant, variantIds, key),
        }
      })

      return { weekStart: weekStart, days: days, header: header, rows: rows }
    }

    function clear() {
      // 進行中リクエストを中断し、cache/inflight/controllers を全リセット（状態の不整合を防ぐ）。
      Object.keys(controllers).forEach(function (k) {
        try {
          if (controllers[k]) controllers[k].abort()
        } catch (e) {}
      })
      cache = {}
      inflight = {}
      controllers = {}
    }

    return {
      fetchWeek: fetchWeek,
      fetchWeekMulti: fetchWeekMulti,
      prefetchAdjacentWeeks: prefetchAdjacentWeeks,
      deriveGrid: deriveGrid,
      deriveGridMulti: deriveGridMulti,
      clear: clear,
    }
  }

  window.ReservationAvailability = {
    create: create,
    // 日付ユーティリティも公開（アプリ側で再利用）
    util: {
      ymd: ymd,
      pad: pad,
      startOfDay: startOfDay,
      addDays: addDays,
      startOfWeek: startOfWeek,
      parseApiDate: parseApiDate,
      parseYmd: parseYmd,
      hhmm: hhmm,
      slotKey: slotKey,
      DAY_MS: DAY_MS,
    },
  }
})()
